import { useEffect, useRef, useState, type RefObject } from 'react'

const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

/**
 * Modal keyboard behavior for the Patients dialogs, as in Conversations:
 * focus moves into the dialog, Tab stays inside it, Escape closes it unless
 * `locked` (a request is in flight and must not be abandoned), and focus
 * returns to whatever opened it.
 */
export function usePatientDialog(
  dialog: RefObject<HTMLElement | null>,
  { onClose, locked = false }: { onClose: () => void; locked?: boolean }
) {
  // Read during the first render, before React applies a field's autoFocus,
  // so it is the control that opened the dialog.
  const [opener] = useState(() =>
    document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null
  )
  const lockedRef = useRef(locked)
  const closeRef = useRef(onClose)
  useEffect(() => {
    lockedRef.current = locked
    closeRef.current = onClose
  })

  useEffect(() => {
    const node = dialog.current
    if (node && !node.contains(document.activeElement)) {
      // The field marked data-autofocus, else the first control.
      ;(
        node.querySelector<HTMLElement>('[data-autofocus]') ??
        node.querySelector<HTMLElement>(FOCUSABLE)
      )?.focus()
    }
    function handleKeyDown(event: KeyboardEvent) {
      const current = dialog.current
      if (!current) return
      if (event.key === 'Escape') {
        if (lockedRef.current) return
        event.preventDefault()
        event.stopPropagation()
        closeRef.current()
        return
      }
      if (event.key !== 'Tab') return
      const items = Array.from(
        current.querySelectorAll<HTMLElement>(FOCUSABLE)
      ).filter((item) => !item.closest('[hidden]'))
      const first = items[0]
      const last = items[items.length - 1]
      if (!first || !last) {
        event.preventDefault()
        return
      }
      const active = document.activeElement
      if (event.shiftKey && (active === first || !current.contains(active))) {
        event.preventDefault()
        last.focus()
      } else if (
        !event.shiftKey &&
        (active === last || !current.contains(active))
      ) {
        event.preventDefault()
        first.focus()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      if (opener?.isConnected) opener.focus()
    }
  }, [dialog, opener])
}
